import React, { useContext, useEffect, useState, useRef } from "react";
import imgLogo from "../assets/icon.png";
import imgUser from "../assets/user.svg";
import { AuthContext, AuthDispatchContext } from "../contexts/authContext";
import { useNavigate } from "react-router-dom";

export default function Header() {
  const authContext = useContext(AuthContext);
  const authDispatchContext = useContext(AuthDispatchContext);
  const navigate = useNavigate();
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const menuElements = [
    {
      url: "/vote",
      title: "Vote"
    },
    {
      url: "/leaderboard",
      title: "Leaderboard"
    },
    {
      url: "/import",
      title: "Import"
    },
    {
      url: "/about",
      title: "About"
    }
  ];

  useEffect(() => {
    if (authContext && authContext.authInfo) {
      setUsername(authContext.authInfo.username);
      setIsSignedIn(true);
    }
  }, [authContext?.authInfo]);

  // Close user menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    }

    if (showUserMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showUserMenu]);

  function getActiveClass(path: string) {
    return currentPath === path ? "active" : "";
  }

  function toggleUserMenu() {
    setShowUserMenu(!showUserMenu);
  }

  function toggleMobileMenu() {
    setShowMobileMenu(!showMobileMenu);
  }

  function logout() {
    if (!authDispatchContext) {
      return;
    }
    authDispatchContext({
      type: "DELETE",
    });
    setUsername("");
    setIsSignedIn(false);
    navigate("/");
  }

  function navigateTo(path: string) {
    setCurrentPath(path);
    setShowMobileMenu(false);
    navigate(path);
  }

  return (
    <header className="container mx-auto px-4">
      <div className="h-16 flex flex-row items-center justify-between">
        {/* Logo */}
        <div className="flex flex-row items-center gap-8">
            <div className="flex-shrink-0">
            <img
              className="hover:cursor-pointer"
              onClick={() => navigateTo("/")} 
              alt="Movie-rater Logo"
              src={imgLogo}
              width={40}
              height={40}  />
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex">
            <ul className="flex h-full font-bold gap-8">
                {menuElements.map((element, key) => (
                  <li
                  key={key}
                  className={"hover:cursor-pointer transition-colors " + getActiveClass(element.url)}
                  onClick={() => navigateTo(element.url)}
                  >
                  {element.title.toUpperCase()}
                  </li>
                ))}
            </ul>
            </nav>
        </div>
        {/* Desktop User Menu */}
        <div className="hidden md:flex items-center gap-3">
          <div className="relative" ref={userMenuRef}>
            <button
              onClick={toggleUserMenu}
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-white bg-opacity-10 border border-white border-opacity-20 hover:bg-opacity-20 transition-all"
            >
              <img alt="User Account icon" src={imgUser} width={24} height={24} />
              <span className="text-sm font-medium">{username}</span>
              <svg 
                className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showUserMenu && (
              isSignedIn ? (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-xl overflow-hidden z-10" style={{background: 'rgba(21, 14, 31, 1)',}}>
                  <div className="px-4 py-3 border-b border-white border-opacity-10">
                    <p className="text-xs text-gray-300">Signed in as</p>
                    <p className="text-sm font-medium text-white truncate">{username}</p>
                  </div>
                  <button
                    onClick={logout}
                    className="w-full text-left px-4 py-3 text-sm text-red-400 hover:bg-white hover:bg-opacity-10 transition-colors"
                  >
                    Log out
                  </button>
                </div>
              ) : (
                <div className="absolute right-0 mt-2 w-48 rounded-md shadow-xl overflow-hidden z-10" style={{background: 'rgba(21, 14, 31, 1)'}}>
                <button
                  onClick={() => navigateTo("/auth/login")}
                  className="w-full text-left px-4 py-3 text-sm hover:bg-white hover:bg-opacity-10 transition-colors"
                >
                  Log in
                </button>
                <button
                  onClick={() => navigateTo("/auth/signup")}
                  className="w-full text-left px-4 py-3 text-md text-orange-400 hover:bg-white hover:bg-opacity-10 transition-colors"
                >
                  Sign up
                </button>
              </div>
              )
            )}
          </div>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={toggleMobileMenu}
          className="md:hidden p-2 rounded-lg hover:bg-white hover:bg-opacity-10 transition-colors"
          aria-label="Toggle menu"
        >
          {showMobileMenu ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="md:hidden absolute left-0 right-0 top-16 shadow-xl z-50" style={{
          background: 'rgba(89, 60, 133, 0.98) 0%',
          backdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <nav className="px-4 py-2">
            <ul className="space-y-1">
              {menuElements.map((element, key) => (
                <li
                  key={key}
                  className={"block px-4 py-3 rounded-lg hover:bg-white hover:bg-opacity-10 cursor-pointer font-bold transition-colors " + getActiveClass(element.url)}
                  onClick={() => navigateTo(element.url)}
                >
                  {element.title.toUpperCase()}
                </li>
              ))}
            </ul>
          </nav>
          {isSignedIn ? (
            <div className="border-t border-white border-opacity-10 px-4 py-3">
              <div className="flex items-center gap-3 px-4 py-2">
                <img alt="User Account icon" src={imgUser} width={32} height={32} />
                <div className="flex-1">
                  <p className="text-xs text-gray-300">Signed in as</p>
                  <p className="text-sm font-medium text-white">{username}</p>
                </div>
              </div>
              <button
                onClick={logout}
                className="w-full mt-2 px-4 py-3 text-left rounded-lg text-sm font-medium text-red-400 hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                Log out
              </button>
            </div>
          ) : (
            <div className="border-t border-white border-opacity-10 px-4 py-3">
              <button
                onClick={() => navigateTo("/auth/login")}
                className="w-full mt-2 px-4 py-3 text-left rounded-lg text-sm font-medium text-400 hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                Log in
              </button>
              <button
                onClick={() => navigateTo("/auth/signup")}
                className="w-full mt-2 px-4 py-3 text-left rounded-lg text-sm font-medium text-orange-400 hover:bg-white hover:bg-opacity-10 transition-colors"
              >
                Sign up
              </button>
            </div>
          )}
        </div>
      )}
    </header>
  );
}